import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Input, InputNumber, Select, message } from 'antd';
import { useTranslation } from 'react-i18next';
import {
  useWaterVolumePerAreas,
  useProductDoses,
} from '../../hooks/useSprayCalculatorData';
import { SprayCalculatorService } from '@client/services/sprayCalculatorService';
import {
  sanitizeDoseInput,
  formatDoseDisplay,
  isRowComplete,
  isRowEmpty,
  filterValidRows,
} from '../../utils/validators';
import { PlusCircleOutlined, MinusCircleOutlined } from '@ant-design/icons';
import { ChevronDown } from 'lucide-react';
import { ProductLabelButton, DeleteRowButton } from '@client/feature/riskCalculator/components/steps/components';
import { HtmlContent } from '@client/utils/html_content';
import { useIsMobile } from '@client/hooks/useIsMobile';
import { MobileSelectSheet } from '@client/components/mobile';

/**
 * Create an empty product row with a unique local ID
 * @returns {Object} Empty product row
 */
function createEmptyRow() {
  return {
    localId: `row_${Date.now()}_${Math.random().toString(36).slice(2, 9)}`,
    productId: '',
    productName: '',
    approvalNumber: '',
    dose: '',
    unitId: '',
    unitName: '',
    formulation: '',
  };
}

/**
 * Step 3 - Treatment Component
 * Water volume configuration and dynamic multi-row product form
 *
 * Features:
 * - Water volume and unit selection
 * - Dynamic product rows with server-side search (debounced)
 * - Dose input with French locale (comma/dot) handling
 * - Dose unit filtering by product formulation
 * - Auto-add empty row when product is selected
 * - Tank mix guidance message
 *
 * @param {Object} props
 * @param {Object} props.selections - Current selections from useSprayCalculator
 * @param {Function} props.setWaterVolume - Setter for water volume
 * @param {Function} props.setWaterVolumeUnitId - Setter for water volume unit
 * @param {Function} props.setProducts - Setter for products array
 * @param {Object} props.enrichedData - Enriched data with product/unit names
 */
const Step3Treatment = React.memo(
  ({ selections, setWaterVolume, setWaterVolumeUnitId, setProducts, enrichedData }) => {
    const { t } = useTranslation('sprayCalculator');

    // --- Data hooks ---
    const { data: waterVolumeUnits = [], isLoading: isLoadingWaterUnits } =
      useWaterVolumePerAreas();

    // --- Product rows local state ---
    const [rows, setRows] = useState(() => {
      // Initialize from URL state if products exist
      const existingProducts = selections.products || [];
      if (existingProducts.length > 0) {
        const restored = existingProducts.map((p) => ({
          localId: `row_${Date.now()}_${Math.random().toString(36).slice(2, 9)}`,
          productId: p.id || p.productId || '',
          productName:
            (enrichedData?.productNames && enrichedData.productNames[p.id || p.productId]) ||
            p.name ||
            p.productName ||
            '',
          approvalNumber: p.approvalNumber || '',
          dose: p.dose || '',
          unitId: p.unitId || p.unit || '',
          unitName:
            (enrichedData?.productDoseUnitNames && enrichedData.productDoseUnitNames[p.id || p.productId]) ||
            p.unitName ||
            '',
          formulation: p.formulation || '',
        }));
        return [...restored, createEmptyRow()];
      }
      return [createEmptyRow()];
    });

    // --- Product search state per row ---
    const [searchStates, setSearchStates] = useState({});
    const searchTimers = useRef({});

    // --- Water volume handlers ---
    const handleWaterVolumeChange = useCallback(
      (value) => {
        setWaterVolume(value);
      },
      [setWaterVolume]
    );

    const handleWaterVolumeUnitChange = useCallback(
      (unitId, option) => {
        setWaterVolumeUnitId(unitId, option?.rawLabel || null);
      },
      [setWaterVolumeUnitId]
    );

    const waterVolumeUnitOptions = useMemo(
      () =>
        waterVolumeUnits.map((item) => ({
          value: item.id,
          label: <HtmlContent html={item.composed_unit_for_client_display || item.name} />,
          rawLabel: item.composed_unit_for_client_display || item.name,
        })),
      [waterVolumeUnits]
    );

    // --- Sync rows to parent ---
    const syncToParent = useCallback(
      (updatedRows) => {
        const validRows = filterValidRows(updatedRows);
        const products = validRows.map((row) => ({
          id: row.productId,
          name: row.productName,
          approvalNumber: row.approvalNumber,
          dose: row.dose,
          unitId: row.unitId,
          unitName: row.unitName,
          formulation: row.formulation,
        }));

        const enrichedProducts = validRows.map((row) => ({
          id: row.productId,
          name: row.productName,
          unitName: row.unitName,
        }));

        setProducts(products, enrichedProducts);
      },
      [setProducts]
    );

    // --- Product search with infinite scroll ---
    const fetchProducts = useCallback(async (localId, searchTerm, onlyAvailable = true, page = 1) => {
      try {
        const params = { page_size: 20, page };
        if (searchTerm) params.query = searchTerm;
        if (onlyAvailable) params.commercially_available = true;

        const { results: rawResults, hasMore } = await SprayCalculatorService.searchCommercialProducts(params);

        const newOptions = (rawResults || []).map((product) => ({
          value: product.id,
          label: `${product.name} (${product.approval_number || ''})`,
          data: product,
        }));

        setSearchStates((prev) => {
          const existing = prev[localId];
          // Append for pagination (page > 1), replace for new search
          const options = page > 1 && existing?.options
            ? [...existing.options, ...newOptions.filter((o) => !existing.options.some((e) => e.value === o.value))]
            : newOptions;

          return {
            ...prev,
            [localId]: { options, loading: false, fetching: false, page, hasMore },
          };
        });
      } catch {
        setSearchStates((prev) => ({
          ...prev,
          [localId]: { ...prev[localId], options: prev[localId]?.options || [], loading: false, fetching: false },
        }));
      }
    }, []);

    const handleProductSearch = useCallback((localId, searchTerm, onlyAvailable = true) => {
      if (searchTimers.current[localId]) {
        clearTimeout(searchTimers.current[localId]);
      }

      setSearchStates((prev) => ({
        ...prev,
        [localId]: { ...prev[localId], loading: true },
      }));

      searchTimers.current[localId] = setTimeout(() => {
        fetchProducts(localId, searchTerm, onlyAvailable, 1);
      }, 300);
    }, [fetchProducts]);

    const handleDropdownOpen = useCallback((localId, open, onlyAvailable = true) => {
      if (open) {
        const state = searchStates[localId];
        if (!state || state.options.length === 0) {
          setSearchStates((prev) => ({
            ...prev,
            [localId]: { ...prev[localId], loading: true },
          }));
          fetchProducts(localId, '', onlyAvailable, 1);
        }
      }
    }, [fetchProducts, searchStates]);

    const handleLoadMore = useCallback((localId, onlyAvailable = true, currentSearch = '') => {
      const state = searchStates[localId];
      if (!state || state.fetching || !state.hasMore) return;

      const nextPage = (state.page || 1) + 1;
      setSearchStates((prev) => ({
        ...prev,
        [localId]: { ...prev[localId], fetching: true },
      }));
      fetchProducts(localId, currentSearch, onlyAvailable, nextPage);
    }, [fetchProducts, searchStates]);

    // Cleanup search timers on unmount
    useEffect(() => {
      const timers = searchTimers.current;
      return () => {
        Object.values(timers).forEach(clearTimeout);
      };
    }, []);

    // --- Product selection handler ---
    const handleProductChange = useCallback(
      (rowIndex, value, option) => {
        setRows((prevRows) => {
          const newRows = [...prevRows];
          const row = { ...newRows[rowIndex] };

          if (!value) {
            row.productId = '';
            row.productName = '';
            row.approvalNumber = '';
            row.formulation = '';
            newRows[rowIndex] = row;
            syncToParent(newRows);
            return newRows;
          }

          // Check for duplicates
          const isDuplicate = newRows.some(
            (r, idx) => idx !== rowIndex && r.productId === value && r.productId !== ''
          );

          if (isDuplicate) {
            message.warning(t('treatment_duplicate_warning'));
            return prevRows;
          }

          row.productId = value;
          row.productName = option?.data?.name || '';
          row.approvalNumber = option?.data?.approval_number || '';
          row.formulation = option?.data?.formulation_type || option?.data?.formulation || '';
          newRows[rowIndex] = row;

          // Auto-add new empty row if this is the last row and now has a product
          if (rowIndex === newRows.length - 1 && row.productId) {
            newRows.push(createEmptyRow());
          }

          syncToParent(newRows);
          return newRows;
        });
      },
      [syncToParent, t]
    );

    // --- Dose change handler ---
    const handleDoseChange = useCallback(
      (rowIndex, inputValue) => {
        setRows((prevRows) => {
          const newRows = [...prevRows];
          const row = { ...newRows[rowIndex] };

          const { storedValue } = sanitizeDoseInput(inputValue);
          row.dose = storedValue;
          newRows[rowIndex] = row;

          // Auto-add row if complete and last
          if (isRowComplete(row) && rowIndex === newRows.length - 1) {
            newRows.push(createEmptyRow());
          }

          syncToParent(newRows);
          return newRows;
        });
      },
      [syncToParent]
    );

    // --- Unit change handler ---
    const handleUnitChange = useCallback(
      (rowIndex, unitId, option) => {
        setRows((prevRows) => {
          const newRows = [...prevRows];
          const row = { ...newRows[rowIndex] };

          row.unitId = unitId;
          row.unitName = option?.rawLabel || '';
          newRows[rowIndex] = row;

          // Auto-add row if complete and last
          if (isRowComplete(row) && rowIndex === newRows.length - 1) {
            newRows.push(createEmptyRow());
          }

          syncToParent(newRows);
          return newRows;
        });
      },
      [syncToParent]
    );

    // --- Delete row handler ---
    const handleDeleteRow = useCallback(
      (rowIndex) => {
        setRows((prevRows) => {
          const newRows = [...prevRows];
          newRows.splice(rowIndex, 1);

          // Always keep at least one empty row
          if (newRows.length === 0) {
            newRows.push(createEmptyRow());
          }

          syncToParent(newRows);
          return newRows;
        });
      },
      [syncToParent]
    );

    // --- Check if first product row has a selection (for tank mix message) ---
    const showTankMixMessage = rows.length > 0 && !isRowEmpty(rows[0]);

    return (
      <div className="flex flex-col gap-4 sm:gap-6 w-full">
        {/* Title */}
        <h3 className="text-xl sm:text-2xl lg:text-[30px] font-medium text-center normal-case">
          {t('treatment_title')}
        </h3>

        {/* Description */}
        <p className="text-xs sm:text-sm text-[#000]">
          {t('treatment_description')}
        </p>

        {/* Section A: Water Volume - two-column like Step 1 */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-6 w-full p-3 sm:p-4 lg:p-6 rounded-2xl bg-[#FAFAFA]">
          <div className="w-full flex flex-col gap-1.5">
            <label className="text-sm font-medium text-[#141414]">
              {t('treatment_water_volume_label')}
            </label>
            <InputNumber
              value={selections.waterVolume ? parseFloat(selections.waterVolume) : null}
              onChange={handleWaterVolumeChange}
              placeholder={t('treatment_water_volume_placeholder')}
              min={0}
              className="w-full"
              size="large"
            />
          </div>

          <div className="w-full flex flex-col gap-1.5">
            <label className="text-sm font-medium text-[#141414]">
              {t('treatment_water_unit_label')}
            </label>
            <Select
              showSearch
              filterOption={false}
              value={selections.waterVolumeUnitId || undefined}
              onChange={handleWaterVolumeUnitChange}
              placeholder={t('treatment_water_unit_placeholder')}
              options={waterVolumeUnitOptions}
              loading={isLoadingWaterUnits}
              className="w-full"
              size="large"
            />
          </div>
        </div>

        {/* Section B: Product Rows */}
        <div className="w-full flex flex-col p-3 sm:p-4 lg:p-6 gap-4 sm:gap-6 rounded-2xl bg-[#FAFAFA]">
          {/* Header Row - Desktop only */}
          <div className="hidden w-full sm:grid sm:grid-cols-[1fr_40px_1fr_1fr_40px] gap-3 items-end font-semibold text-sm">
            <div>{t('treatment_product_label')}</div>
            <div></div>
            <div>{t('treatment_dose_label')}</div>
            <div>{t('treatment_dose_unit_label')}</div>
            <div></div>
          </div>

          {/* Product Rows */}
          {rows.map((row, index) => (
            <React.Fragment key={row.localId}>
              {/* Separator between rows on mobile */}
              {index > 0 && (
                <div className="w-full h-px bg-gray-200 lg:hidden" />
              )}

              <ProductRow
                row={row}
                index={index}
                searchState={searchStates[row.localId]}
                onProductSearch={handleProductSearch}
                onDropdownOpen={handleDropdownOpen}
                onLoadMore={handleLoadMore}
                onProductChange={handleProductChange}
                onDoseChange={handleDoseChange}
                onUnitChange={handleUnitChange}
                onDelete={handleDeleteRow}
                t={t}
              />

              {/* Tank mix message - after first product is selected */}
              {index === 0 && showTankMixMessage && (
                <div className="w-full">
                  <p
                    className="text-sm sm:text-base font-bold leading-[125%]"
                    style={{
                      color: 'var(--Neon-9, #105235)',
                      fontFamily: 'Roboto',
                    }}
                  >
                    {t('treatment_tank_mix')}
                  </p>
                </div>
              )}
            </React.Fragment>
          ))}
        </div>
      </div>
    );
  }
);

Step3Treatment.displayName = 'Step3Treatment';

// ─── Product Row Sub-Component ────────────────────────────────────────────────

/**
 * Individual product row - extracted for clarity
 * Renders both mobile (vertical stack) and desktop (grid) layouts
 */
const ProductRow = React.memo(
  ({
    row,
    index,
    searchState,
    onProductSearch,
    onDropdownOpen,
    onLoadMore,
    onProductChange,
    onDoseChange,
    onUnitChange,
    onDelete,
    t,
  }) => {
    const {
      options: searchOptions = [],
      loading: isSearching = false,
      fetching: isFetchingMore = false,
      hasMore = false,
    } = searchState || {};

    // --- Commercial availability toggle (state lives here, like UnifiedCascadingSelect) ---
    const [showOnlyAvailable, setShowOnlyAvailable] = useState(true);
    const currentSearchRef = useRef('');

    const handleToggleAvailability = useCallback((e) => {
      if (e) { e.preventDefault(); e.stopPropagation(); }
      setShowOnlyAvailable((prev) => {
        const newValue = !prev;
        onProductSearch(row.localId, currentSearchRef.current, newValue);
        return newValue;
      });
    }, [onProductSearch, row.localId]);

    // Infinite scroll handler
    const handlePopupScroll = useCallback((e) => {
      if (!hasMore || isFetchingMore) return;
      const { target } = e;
      if (target.scrollTop + target.clientHeight >= target.scrollHeight - 50) {
        onLoadMore(row.localId, showOnlyAvailable, currentSearchRef.current);
      }
    }, [hasMore, isFetchingMore, onLoadMore, row.localId, showOnlyAvailable]);

    const dropdownRender = useCallback((menu) => (
      <>
        {menu}
        {isFetchingMore && (
          <div style={{ padding: 8, textAlign: 'center', borderTop: '1px solid #f0f0f0' }}>
            <span className="text-xs text-[#595959]">{t('loading')}</span>
          </div>
        )}
        <div
          style={{
            borderTop: '1px solid #f0f0f0',
            padding: '8px 12px',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            gap: 8,
            color: '#105235',
            fontSize: 13,
            fontWeight: 500,
            backgroundColor: '#fafafa',
          }}
          onMouseDown={(e) => { e.preventDefault(); e.stopPropagation(); }}
          onClick={handleToggleAvailability}
        >
          {showOnlyAvailable ? (
            <PlusCircleOutlined style={{ fontSize: 16 }} />
          ) : (
            <MinusCircleOutlined style={{ fontSize: 16 }} />
          )}
          <span>
            {showOnlyAvailable
              ? t('treatment_show_all_products')
              : t('treatment_show_available_products')}
          </span>
        </div>
      </>
    ), [showOnlyAvailable, isFetchingMore, handleToggleAvailability, t]);

    // Fetch dose units, filtered by the product's formulation if available
    const doseParams = useMemo(
      () => (row.formulation ? { unit_form: row.formulation } : {}),
      [row.formulation]
    );
    const { data: doseUnits = [], isLoading: isLoadingDoseUnits } = useProductDoses(doseParams);

    const doseUnitOptions = useMemo(
      () =>
        doseUnits.map((unit) => ({
          value: unit.id,
          label: <HtmlContent html={unit.composed_unit_for_client_display || unit.name} />,
          rawLabel: unit.composed_unit_for_client_display || unit.name,
        })),
      [doseUnits]
    );

    const isMobile = useIsMobile();
    const [sheetOpen, setSheetOpen] = useState(false);

    // Build the display value for the product select when a product is already chosen
    const productDisplayValue = row.productId
      ? row.productId
      : undefined;

    const productDisplayLabel = row.productId && row.productName
      ? `${row.productName}${row.approvalNumber ? ` (${row.approvalNumber})` : ''}`
      : '';

    // Options to show: if product already selected, include it so Ant Select can display label
    const selectOptions = useMemo(() => {
      if (row.productId && row.productName) {
        const currentOption = {
          value: row.productId,
          label: `${row.productName}${row.approvalNumber ? ` (${row.approvalNumber})` : ''}`,
          data: row,
        };
        const filtered = searchOptions.filter((opt) => opt.value !== row.productId);
        return [currentOption, ...filtered];
      }
      return searchOptions;
    }, [row.productId, row.productName, row.approvalNumber, searchOptions]);

    const handleMobileChange = useCallback((value, option) => {
      onProductChange(index, value, option);
      setSheetOpen(false);
    }, [onProductChange, index]);

    const handleMobileToggle = useCallback(() => {
      setShowOnlyAvailable((prev) => {
        const newValue = !prev;
        onProductSearch(row.localId, currentSearchRef.current, newValue);
        return newValue;
      });
    }, [onProductSearch, row.localId]);

    const handleMobileSearch = useCallback((term) => {
      currentSearchRef.current = term;
      onProductSearch(row.localId, term, showOnlyAvailable);
    }, [onProductSearch, row.localId, showOnlyAvailable]);

    const toggleLabel = showOnlyAvailable
      ? t('treatment_show_all_products')
      : t('treatment_show_available_products');

    return (
      <div className="w-full grid grid-cols-1 sm:grid-cols-[1fr_40px_1fr_1fr_40px] gap-3 sm:items-center">
        {/* Product Select */}
        <div className="min-w-0">
          <label className="block text-xs font-medium text-[#595959] mb-1 sm:hidden">
            {t('treatment_product_label')}
          </label>
          {isMobile ? (
            <>
              <div
                onClick={() => { setSheetOpen(true); onDropdownOpen(row.localId, true, showOnlyAvailable); }}
                style={{
                  width: '100%',
                  minHeight: 44,
                  padding: '8px 12px',
                  border: '1px solid #d9d9d9',
                  borderRadius: 6,
                  backgroundColor: '#fff',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  color: productDisplayLabel ? '#262626' : '#bfbfbf',
                }}
              >
                <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', flex: 1 }}>
                  {productDisplayLabel || t('treatment_product_placeholder')}
                </span>
                <ChevronDown size={16} style={{ marginLeft: 8, color: '#bfbfbf', flexShrink: 0 }} />
              </div>
              <MobileSelectSheet
                isOpen={sheetOpen}
                onClose={() => setSheetOpen(false)}
                title={t('treatment_product_label')}
                options={selectOptions}
                value={productDisplayValue}
                onChange={handleMobileChange}
                loading={isSearching || isFetchingMore}
                onSearch={handleMobileSearch}
                onLoadMore={() => onLoadMore(row.localId, showOnlyAvailable, currentSearchRef.current)}
                hasMore={hasMore}
                showAllToggle={{
                  enabled: true,
                  label: toggleLabel,
                  active: !showOnlyAvailable,
                  onClick: handleMobileToggle,
                }}
                placeholder={t('treatment_product_placeholder')}
              />
            </>
          ) : (
            <Select
              showSearch
              value={productDisplayValue}
              onSearch={(term) => { currentSearchRef.current = term; onProductSearch(row.localId, term, showOnlyAvailable); }}
              onDropdownVisibleChange={(open) => { if (!open) currentSearchRef.current = ''; onDropdownOpen(row.localId, open, showOnlyAvailable); }}
              onPopupScroll={handlePopupScroll}
              onChange={(value, option) => onProductChange(index, value, option)}
              placeholder={t('treatment_product_placeholder')}
              filterOption={false}
              options={selectOptions}
              loading={isSearching}
              notFoundContent={isSearching ? t('loading') : t('treatment_no_products_found')}
              dropdownRender={dropdownRender}
              allowClear
              className="w-full"
              size="large"
            />
          )}
        </div>

        {/* Label Icon */}
        <div className="flex sm:justify-center">
          <ProductLabelButton
            approvalNumber={row.approvalNumber}
            tooltip={t('treatment_product_label_tooltip')}
          />
        </div>

        {/* Dose Input */}
        <div className="min-w-0">
          <label className="block text-xs font-medium text-[#595959] mb-1 sm:hidden">
            {t('treatment_dose_label')}
          </label>
          <Input
            value={formatDoseDisplay(row.dose)}
            onChange={(e) => onDoseChange(index, e.target.value)}
            placeholder={t('treatment_dose_placeholder')}
            disabled={!row.productId}
            inputMode="decimal"
            className="w-full"
            size="large"
          />
        </div>

        {/* Dose Unit Select */}
        <div className="min-w-0">
          <label className="block text-xs font-medium text-[#595959] mb-1 sm:hidden">
            {t('treatment_dose_unit_label')}
          </label>
          <Select
            showSearch
            filterOption={false}
            value={row.unitId || undefined}
            onChange={(value, option) => onUnitChange(index, value, option)}
            placeholder={t('treatment_dose_unit_placeholder')}
            options={doseUnitOptions}
            loading={isLoadingDoseUnits}
            disabled={!row.productId}
            className="w-full"
            size="large"
          />
        </div>

        {/* Delete Icon */}
        <div className="flex sm:justify-center">
          {!isRowEmpty(row) && (
            <DeleteRowButton
              onDelete={() => onDelete(index)}
              tooltip={t('treatment_delete_product')}
              confirmTitle={t('treatment_delete_confirm_title')}
              confirmDescription={t('treatment_delete_confirm_description')}
              confirmOk={t('treatment_delete_confirm_ok')}
              confirmCancel={t('treatment_delete_confirm_cancel')}
            />
          )}
        </div>
      </div>
    );
  }
);

ProductRow.displayName = 'ProductRow';

export default Step3Treatment;
